import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Box, IconButton, Link, Paper, Popper, Tooltip, Typography } from '@material-ui/core';
import CancelIcon from '@material-ui/icons/Cancel';
import CheckCircleIcon from '@material-ui/icons/CheckCircle';
import DeleteOutlinedIcon from '@material-ui/icons/DeleteOutlined';
import EditOutlinedIcon from '@material-ui/icons/EditOutlined';
import ErrorIcon from '@material-ui/icons/Error';
import FiberNewIcon from '@material-ui/icons/FiberNew';
import FileCopyOutlinedIcon from '@material-ui/icons/FileCopyOutlined';
import InfoIcon from '@material-ui/icons/Info';
import LockIcon from '@material-ui/icons/Lock';
import PlayArrowOutlinedIcon from '@material-ui/icons/PlayArrowOutlined';
import StopOutlinedIcon from '@material-ui/icons/StopOutlined';
import WarningIcon from '@material-ui/icons/Warning';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';

import { ReactComponent as MoreIcon } from 'assets/icons/more.svg';
import ContextMenu from 'components/ContextMenu/ContextMenu';
import Table from 'components/Table';
import { actionTemplatesApi } from 'config/api/actionLibrary/actionLibrary';
import { ACTION_TEMPLATE_OWNER_TYPE_TO_MESSAGE_KEY } from 'config/api/actionLibrary/actionLibrary.types';
import type { ActionTemplate } from 'config/api/actionLibrary/actionLibrary.types';
import action_library_messages from 'messages/action_library_messages';
import ActionTemplateSummaryPopoverContent from 'pages/ActionLibrary/_components/ActionTemplateSummaryPopoverContent/ActionTemplateSummaryPopoverContent';
import useActionTemplateCopy from 'pages/ActionLibrary/hooks/useActionTemplateCopy';
import useCanEditActionTemplate from 'pages/ActionLibrary/hooks/useCanEditActionTemplate';
import useCanToggleActiveActionTemplate from 'pages/ActionLibrary/hooks/useCanToggleActiveActionTemplate';
import useIsActionTemplateOwner from 'pages/ActionLibrary/hooks/useIsActionTemplateOwner';
import { useConfirmationModalContext } from 'reactContext/ConfirmationModalContext/ConfirmationModalContext';
import PATHS from 'router/PATHS';
import { TableDataMappingRow } from 'types/Table';

import useStyles from './ActionTemplatesTable.styles';

type Props = {
  data: any[] | null;
  loading: boolean;
  sort: { key: string | null; direction: string | null };
  onSort: (key: string, direction: string) => void;
  refreshData: () => void;
  filtersService?: any;
  onSearch?: (searchTerm: string) => void;
  searchValue?: string;
};

const HOVER_POPOVER_DELAY_MS = 400;
const CLOSE_POPOVER_DELAY_MS = 150;
/** Ignore cell mouseLeave only briefly after opening (avoids close from re-render; real leave is later). */
const IGNORE_LEAVE_AFTER_OPEN_MS = 80;

const ActionTemplatesTable: React.FC<Props> = ({ data, loading, sort, onSort, refreshData, filtersService, onSearch, searchValue }) => {
  const { t } = useTranslation();
  const styles = useStyles();
  const history = useHistory();
  const { showConfirmationModal } = useConfirmationModalContext();
  const { enqueueSnackbar } = useSnackbar();
  const { copyActionTemplate } = useActionTemplateCopy();
  const isActionTemplateOwner = useIsActionTemplateOwner();
  const canEdit = useCanEditActionTemplate();
  const canToggleActive = useCanToggleActiveActionTemplate();

  const [popoverAnchor, setPopoverAnchor] = useState<HTMLElement | null>(null);
  const [popoverTemplate, setPopoverTemplate] = useState<ActionTemplate | null>(null);
  const openTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openedAtRef = useRef<number>(0);
  const anchorRef = useRef<HTMLElement | null>(null);
  const popoverContentRef = useRef<HTMLDivElement | null>(null);

  const closePopover = useCallback(() => {
    openedAtRef.current = 0;
    anchorRef.current = null;
    setPopoverAnchor(null);
    setPopoverTemplate(null);
  }, []);

  useEffect(() => {
    if (!popoverAnchor) return undefined;
    const handleDocMouseMove = (e: MouseEvent) => {
      const target = e.target as Node;
      const overAnchor = anchorRef.current?.contains(target);
      const overPopover = popoverContentRef.current?.contains(target);
      if (!overAnchor && !overPopover) {
        closePopover();
      }
    };
    document.addEventListener('mousemove', handleDocMouseMove);
    return () => {
      document.removeEventListener('mousemove', handleDocMouseMove);
    };
  }, [popoverAnchor, closePopover]);

  const handleNameCellMouseEnter = useCallback((event: React.MouseEvent<HTMLElement>, template: ActionTemplate) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    const anchorEl = event.currentTarget;
    openTimeoutRef.current = setTimeout(() => {
      openTimeoutRef.current = null;
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = null;
      }
      openedAtRef.current = Date.now();
      anchorRef.current = anchorEl.closest('td') || anchorEl;
      setPopoverAnchor(anchorEl);
      setPopoverTemplate(template);
    }, HOVER_POPOVER_DELAY_MS);
  }, []);

  const handleNameCellMouseLeave = useCallback(() => {
    if (openTimeoutRef.current) {
      clearTimeout(openTimeoutRef.current);
      openTimeoutRef.current = null;
    }
    if (Date.now() - openedAtRef.current < IGNORE_LEAVE_AFTER_OPEN_MS) {
      return;
    }
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      closePopover();
    }, CLOSE_POPOVER_DELAY_MS);
  }, [closePopover]);

  const handlePopoverMouseEnter = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
  }, []);

  const handlePopoverMouseLeave = useCallback(() => {
    closePopover();
  }, [closePopover]);

  const handlePopoverClose = useCallback(() => {
    closePopover();
  }, [closePopover]);

  const handlePopoverClick = useCallback(() => {
    if (popoverTemplate) {
      history.push(PATHS.ACTION_LIBRARY_DETAIL.replace(':id', popoverTemplate.id.toString()));
      handlePopoverClose();
    }
  }, [popoverTemplate, history, handlePopoverClose]);

  const parsedData = useMemo(() => {
    if (!data) return null;
    return data;
  }, [data]);

  const isLocked = useCallback(
    (template: ActionTemplate) => {
      if (isActionTemplateOwner(template)) return false;
      return !!template.isLocked;
    },
    [isActionTemplateOwner],
  );

  const handleRowClick = (event: React.MouseEvent, template: ActionTemplate) => {
    history.push(PATHS.ACTION_LIBRARY_DETAIL.replace(':id', template.id.toString()));
  };

  const handleDelete = useCallback(
    async (template: ActionTemplate) => {
      const confirmed = await showConfirmationModal({
        title: t(action_library_messages.delete_confirmation.title),
        body: t(action_library_messages.delete_confirmation.body),
      });

      if (confirmed) {
        try {
          await actionTemplatesApi.deleteActionTemplate(template.id);
          enqueueSnackbar(t(action_library_messages.success.deleted), { variant: 'success' });
          refreshData();
        } catch (error) {
          enqueueSnackbar(t(action_library_messages.errors.template_delete_failed), { variant: 'error' });
        }
      }
    },
    [showConfirmationModal, t, enqueueSnackbar, refreshData],
  );

  const handleCopy = useCallback(
    async (listTemplate: ActionTemplate) => {
      await copyActionTemplate(listTemplate);
    },
    [copyActionTemplate],
  );

  const handleToggleActive = useCallback(
    async (template: ActionTemplate) => {
      try {
        const newStatus = template.status === 'active' ? 'inactive' : 'active';
        await actionTemplatesApi.updateActionTemplate(template.id, { status: newStatus });
        enqueueSnackbar(
          newStatus === 'active' ? t(action_library_messages.success.activated) : t(action_library_messages.success.deactivated),
          { variant: 'success' },
        );
        refreshData();
      } catch (error) {
        enqueueSnackbar(t(action_library_messages.errors.template_status_update_failed), { variant: 'error' });
      }
    },
    [enqueueSnackbar, t, refreshData],
  );

  const handleEdit = useCallback(
    (template: ActionTemplate) => {
      history.push(PATHS.ACTION_LIBRARY_EDIT.replace(':id', template.id.toString()));
    },
    [history],
  );

  const renderContextMenu = useCallback(
    (template: ActionTemplate) => {
      const elements = [
        {
          label: template.status === 'active' ? t(action_library_messages.actions.deactivate) : t(action_library_messages.actions.activate),
          onClick: () => handleToggleActive(template),
          disabled: !canToggleActive(template),
          icon: template.status === 'active' ? <StopOutlinedIcon fontSize='small' /> : <PlayArrowOutlinedIcon fontSize='small' />,
        },
        {
          label: t(action_library_messages.actions.edit),
          onClick: () => handleEdit(template),
          disabled: isLocked(template) || !canEdit(template),
          icon: <EditOutlinedIcon fontSize='small' />,
        },
        {
          label: t(action_library_messages.actions.copy),
          onClick: () => handleCopy(template),
          icon: <FileCopyOutlinedIcon fontSize='small' />,
        },
        {
          label: t(action_library_messages.actions.delete),
          onClick: () => handleDelete(template),
          disabled: isLocked(template) || !canEdit(template),
          icon: <DeleteOutlinedIcon fontSize='small' />,
        },
      ];

      return (
        <ContextMenu elements={elements} id={`action-template-${template.id}`}>
          <IconButton>
            <MoreIcon />
          </IconButton>
        </ContextMenu>
      );
    },
    [handleToggleActive, handleEdit, handleCopy, handleDelete, canToggleActive, t],
  );

  const renderLockedIcon = (template: ActionTemplate) => (
    <div style={{ marginRight: 8, display: 'flex' }}>
      {template.lockedByUser ? (
        <Tooltip
          interactive
          placement='right'
          title={
            <Box>
              <Typography style={{ fontWeight: 'bold', display: 'block' }} variant='caption'>
                Edited and locked by: {template.lockedByUser.fullName}
              </Typography>
              <Link
                color='inherit'
                href={`mailto:${template.lockedByUser.email}`}
                onClick={(e: React.MouseEvent) => e.stopPropagation()}
                variant='caption'
              >
                {template.lockedByUser.email}
              </Link>
            </Box>
          }
        >
          <LockIcon color='error' style={{ fontSize: 16 }} />
        </Tooltip>
      ) : (
        <Tooltip title={t(action_library_messages.table.locked_tooltip)}>
          <LockIcon color='error' style={{ fontSize: 16 }} />
        </Tooltip>
      )}
    </div>
  );

  const dataMapping: TableDataMappingRow<ActionTemplate>[] = useMemo(
    () => [
      {
        id: 'name',
        width: '30%',
        label: t(action_library_messages.table.name),
        get: (template: ActionTemplate) => (
          <span
            onMouseEnter={(e: React.MouseEvent<HTMLElement>) => handleNameCellMouseEnter(e, template)}
            onMouseLeave={handleNameCellMouseLeave}
            role='presentation'
            style={{ display: 'block', width: '100%', minHeight: '100%' }}
          >
            <div style={{ display: 'flex', alignItems: 'center' }}>
              {template.isFreshChange && (
                <Tooltip title={t(action_library_messages.table.fresh_changes_tooltip)}>
                  <FiberNewIcon color='secondary' fontSize='small' style={{ marginRight: 8 }} />
                </Tooltip>
              )}
              {template.isLocked && renderLockedIcon(template)}
              {template.name}
            </div>
          </span>
        ),
      },
      {
        id: 'area',
        width: '12%',
        label: t(action_library_messages.table.area),
        get: (template: ActionTemplate) => template.areaData?.displayName || template.area,
      },
      {
        id: 'sub_area',
        width: '12%',
        label: t(action_library_messages.table.subarea),
        get: (template: ActionTemplate) => template.subAreaData?.name || template.subArea || '-',
      },
      {
        id: 'status',
        width: '10%',
        label: t(action_library_messages.table.status),
        get: (template: ActionTemplate) => (
          <div className={styles.statusCell}>
            {template.status === 'active' ? (
              <CheckCircleIcon className={`${styles.statusIcon} ${styles.activeIcon}`} />
            ) : (
              <CancelIcon className={`${styles.statusIcon} ${styles.inactiveIcon}`} />
            )}
            <span>{t(action_library_messages.status[template.status])}</span>
          </div>
        ),
      },
      {
        id: 'criticality',
        width: '10%',
        label: t(action_library_messages.table.criticality),
        get: (template: ActionTemplate) => {
          const criticalityKey = template.criticality.toUpperCase() as 'LOW' | 'MEDIUM' | 'HIGH';
          let IconComponent;
          let iconClass;

          if (template.criticality === 'low') {
            IconComponent = InfoIcon;
            iconClass = styles.criticalityLowIcon;
          } else if (template.criticality === 'medium') {
            IconComponent = WarningIcon;
            iconClass = styles.criticalityMediumIcon;
          } else {
            IconComponent = ErrorIcon;
            iconClass = styles.criticalityHighIcon;
          }

          return (
            <div className={styles.statusCell}>
              <IconComponent className={`${styles.statusIcon} ${iconClass}`} />
              <span>{t(action_library_messages.criticality[criticalityKey])}</span>
            </div>
          );
        },
      },
      {
        id: 'creator',
        width: '10%',
        label: t(action_library_messages.table.creator),
        get: (template: ActionTemplate) =>
          t(action_library_messages.owner_type[ACTION_TEMPLATE_OWNER_TYPE_TO_MESSAGE_KEY[template.ownerType]]),
      },
      {
        id: 'actions',
        width: '6%',
        label: '',
        isAddon: true,
        get: (template: ActionTemplate) => renderContextMenu(template),
      },
    ],
    [t, renderContextMenu, styles, handleNameCellMouseEnter, handleNameCellMouseLeave],
  );

  return (
    <>
      <Popper anchorEl={popoverAnchor} open={!!popoverAnchor} placement='bottom-start' style={{ zIndex: 1300 }}>
        <Paper elevation={4} onMouseEnter={handlePopoverMouseEnter} onMouseLeave={handlePopoverMouseLeave}>
          <div
            ref={popoverContentRef}
            onClick={handlePopoverClick}
            onKeyDown={(e: React.KeyboardEvent) => e.key === 'Enter' && handlePopoverClick()}
            role='button'
            style={{ cursor: 'pointer' }}
            tabIndex={0}
          >
            {popoverTemplate ? <ActionTemplateSummaryPopoverContent template={popoverTemplate} /> : null}
          </div>
        </Paper>
      </Popper>
      <Table
        clickableRow
        data={parsedData}
        dataMapping={dataMapping}
        enableSearch={!!onSearch}
        filtersService={filtersService}
        onRowClick={handleRowClick}
        onSearch={onSearch}
        onSort={onSort}
        refetching={loading}
        searchValue={searchValue}
        sort={sort}
      />
    </>
  );
};

export default ActionTemplatesTable;
